import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { BellIcon, BellSlashIcon } from '@heroicons/react/24/outline';
import { theme } from '../../styles/theme';
import api from '../../services/api';
import toast from 'react-hot-toast';

const Container = styled.div`
  max-width: 800px;
`;

const Section = styled.div`
  background: ${theme.colors.background.card};
  border-radius: ${theme.borderRadius.lg};
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid ${theme.colors.border.primary};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: start;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: ${theme.colors.text.primary};
  margin: 0 0 0.5rem 0;
  font-weight: 600;
`;

const SectionDescription = styled.p`
  color: ${theme.colors.text.secondary};
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.6;
`;

const SettingItem = styled.div`
  padding: 1.5rem;
  background: ${theme.colors.background.tertiary};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: 1rem;
  border: 1px solid ${theme.colors.border.primary};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SettingHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.hasDescription ? '0.75rem' : '0'};
`;

const SettingLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${theme.colors.text.primary};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${theme.colors.primary.main};
  }
`;

const SettingDescription = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.5;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 3.5rem;
  height: 2rem;
  cursor: pointer;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  span {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.checked ? theme.colors.primary.main : theme.colors.background.primary};
    border-radius: ${theme.borderRadius.full};
    transition: all 0.3s ease;
    border: 2px solid ${props => props.checked ? theme.colors.primary.main : theme.colors.border.primary};
    
    &:before {
      content: "";
      position: absolute;
      height: 1.5rem;
      width: 1.5rem;
      left: 0.125rem;
      bottom: 0.125rem;
      background: white;
      border-radius: 50%;
      transition: all 0.3s ease;
      transform: ${props => props.checked ? 'translateX(1.5rem)' : 'translateX(0)'};
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid ${theme.colors.border.primary};
`;

const Button = styled.button`
  padding: 0.875rem 2rem;
  background: ${props => props.variant === 'primary' ? theme.colors.primary.main : theme.colors.background.tertiary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;
  
  &:hover {
    background: ${props => props.variant === 'primary' ? '#2563eb' : theme.colors.background.primary};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const InfoBox = styled.div`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: ${theme.borderRadius.md};
  padding: 1rem;
  margin-top: 1.5rem;
  
  h4 {
    color: ${theme.colors.primary.main};
    margin: 0 0 0.5rem 0;
    font-size: 0.9375rem;
    font-weight: 600;
  }
  
  p {
    color: ${theme.colors.text.secondary};
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.5;
  }
`;

const SignalKNotificationSettings = () => {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/signalk-preferences');
      setPreferences(response.data.preferences || {
        notify_on_disconnect: true,
        notify_on_reconnect: true,
        disconnect_delay_minutes: 5,
        min_downtime_for_reconnect_minutes: 5
      });
    } catch (error) {
      console.error('Error loading SignalK preferences:', error);
      // Set defaults if no preferences exist
      setPreferences({
        notify_on_disconnect: true,
        notify_on_reconnect: true,
        disconnect_delay_minutes: 5,
        min_downtime_for_reconnect_minutes: 5
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/api/signalk-preferences', preferences);
      toast.success('SignalK notification settings saved!');
    } catch (error) {
      console.error('Error saving SignalK preferences:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (field) => {
    setPreferences(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  if (loading || !preferences) {
    return (
      <Container>
        <Section>
          <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary }}>
            Loading preferences...
          </div>
        </Section>
      </Container>
    );
  }

  return (
    <Container>
      <Section>
        <SectionHeader>
          <BellIcon style={{ width: '2rem', height: '2rem', color: theme.colors.primary.main }} />
          <div>
            <SectionTitle>SignalK Connection Alerts</SectionTitle>
            <SectionDescription>
              Manage email notifications for your vessel's live data feed status
            </SectionDescription>
          </div>
        </SectionHeader>

        {/* Disconnect Notification */}
        <SettingItem>
          <SettingHeader hasDescription>
            <SettingLabel htmlFor="notify_disconnect">
              <BellSlashIcon />
              Disconnection Alerts
            </SettingLabel>
            <Toggle checked={preferences.notify_on_disconnect}>
              <input
                type="checkbox"
                id="notify_disconnect"
                checked={preferences.notify_on_disconnect}
                onChange={() => handleToggle('notify_on_disconnect')}
              />
              <span />
            </Toggle>
          </SettingHeader>
          <SettingDescription>
            Receive an email when your vessel's live data feed stops streaming. You'll be notified that 
            your dashboard has switched to showing database values instead of real-time data.
          </SettingDescription>
        </SettingItem>

        {/* Reconnect Notification */}
        <SettingItem>
          <SettingHeader hasDescription>
            <SettingLabel htmlFor="notify_reconnect">
              <BellIcon />
              Reconnection Alerts
            </SettingLabel>
            <Toggle checked={preferences.notify_on_reconnect}>
              <input
                type="checkbox"
                id="notify_reconnect"
                checked={preferences.notify_on_reconnect}
                onChange={() => handleToggle('notify_on_reconnect')}
              />
              <span />
            </Toggle>
          </SettingHeader>
          <SettingDescription>
            Get a confirmation email when your vessel's live data feed comes back online. The email will 
            include downtime duration and confirm your dashboard is showing live data again.
          </SettingDescription>
        </SettingItem>

        <InfoBox>
          <h4>💡 How Connection Alerts Work</h4>
          <p>
            <strong>Disconnection:</strong> If your SignalK server or plugin stops sending data, we'll notify you 
            so you know your dashboard is showing historical data instead of live updates.
            <br/><br/>
            <strong>Reconnection:</strong> Once the connection is restored, we'll send a confirmation with 
            downtime stats so you know everything is back online.
            <br/><br/>
            <strong>Dashboard Indicators:</strong> Your widgets always show ⚡ for live data or 💾 for database data, 
            regardless of email notification settings.
          </p>
        </InfoBox>

        <ButtonGroup>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </ButtonGroup>
      </Section>
    </Container>
  );
};

export default SignalKNotificationSettings;

